import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle
} from '@headlessui/react';
import { Input } from '../Input/Input';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { Button } from '../Button/Button';
import { XCloseModal } from '../../../icons/SvgIcons';
import { authService } from '../../../services/AuthService';
import { deleteCurrentUser } from '../../../services/UserService';
import { SecondaryButton, WhiteButton } from '../Button/Buttons';
import { isOidcAuth } from '../../../config/featureFlags';
import { PATHS } from '../../../constants/Paths.constants';
import { logoutAndClearStorage } from '../../../utils/Auth.util';
import { getApiErrorMessage, isApiErrorCode } from '../../../utils/apiError';

export default function ModalDeleteAccount() {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [buttonText, setButtonText] = useState('Delete Account');
  const [deleteError, setDeleteError] = useState('');

  const { user } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const closeModal = () => {
    setOpen(false);
    setDeleteError('');
    // Reopening must re-arm from scratch. Without this the typed phrase survives
    // a cancel, so the dialog returns with the confirmation already filled in and
    // the Delete button already enabled — one click from an irreversible action.
    // More reachable since the last-admin refusal now keeps the dialog open,
    // which makes Cancel the usual exit from a failed attempt.
    setConfirmDelete('');
  };

  const handleDelete = async () => {
    if (confirmDelete !== 'delete my account') return;

    setDeleteError('');

    try {
      setButtonText('Deleting...');

      await deleteCurrentUser(confirmDelete);

      // Account deleted → terminate the session through the same canonical
      // logout path as the header "Logout" button, so no stale auth state or
      // OIDC token lingers in redux/sessionStorage. Otherwise the still-valid
      // (but backend-rejected) access token keeps the UI looking logged in.
      if (isOidcAuth()) {
        // LogoutPage handles OIDC end-session, userManager.removeUser()
        // and persistor.purge().
        navigate(PATHS.LOGOUT, { replace: true });
      } else {
        await authService.logout();
        dispatch(logoutAndClearStorage());
        navigate(PATHS.LOGIN, { replace: true });
      }
    } catch (error) {
      // Matched by code, never by status or message. The previous check keyed on
      // HTTP 400 plus the exact sentence 'Cannot delete the last admin of the
      // platform', and went dead twice over: the refusal moved to 409 AND the
      // wording was rewritten. That is the failure apiError.ts warns about —
      // those strings are user-facing copy and are localized server-side.
      //
      // The dialog stays open either way. The delete did not happen, the user is
      // still signed in, and a last-admin refusal is correctable: promote another
      // admin and retry. The old code closed the dialog silently here, which left
      // people believing an account had been deleted when it had not.
      if (isApiErrorCode(error, 'CANNOT_DELETE_LAST_ADMIN')) {
        setDeleteError(
          getApiErrorMessage(
            error,
            'You are the last admin of this organization. Promote another admin before deleting your account.'
          )
        );
        return;
      }

      setDeleteError(
        getApiErrorMessage(
          error,
          'Deleting your account failed. Please try again.'
        )
      );
    } finally {
      setButtonText('Delete Account');
    }
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center justify-center w-[111px] py-[7px] px-[11px] h-[30px] text-white text-xs font-medium bg-rose-500 rounded-full shadow-sm hover:bg-rose-600 focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rose-600  outline-none"
      >
        Delete Account
      </button>
      <Dialog open={open} onClose={() => {}} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-[rgba(24,24,26,0.4)] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="flex flex-col gap-6 relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all p-6 data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="bg-white">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <div className="flex items-center justify-between">
                      <DialogTitle
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        Delete account
                      </DialogTitle>
                      <Button buttonClassName="" onClick={closeModal}>
                        {XCloseModal()}
                      </Button>
                    </div>
                    <div className="flex flex-col gap-6 mt-2 text-sm text-gray-500">
                      <p>
                        <span className="font-semibold">
                          Are you sure you want to delete your EU AI Audit
                          account?{' '}
                        </span>
                        Deleting your {user?.email} account will permanently
                        remove your personal data, including risk assessments,
                        reports, and shared content. This action cannot be
                        undone.
                      </p>
                      <p>
                        Any AI systems currently assigned to your account must
                        have an owner to maintain compliance continuity. Before
                        deleting your account, you can{' '}
                        <span className="font-semibold">
                          transfer ownership
                        </span>{' '}
                        of your AI systems to another account from the AI
                        Systems Overview page. Otherwise, ownership will be
                        automatically transferred to the Administration team.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <Input
                id="deleteAccount"
                label="To confirm, please type “delete my account” below."
                maxLength={17}
                onChange={(e) => {
                  setConfirmDelete(e.target.value);
                  if (deleteError) setDeleteError('');
                }}
                value={confirmDelete}
                containerClassName="flex flex-col items-start gap-1 self-stretch"
                labelClassName="label"
                inputClassName="input"
              />
              {deleteError && (
                <p className="text-sm text-rose-800 text-left">{deleteError}</p>
              )}
              <div className="flex flex-row-reverse gap-3">
                <SecondaryButton
                  onClick={handleDelete}
                  disabled={confirmDelete !== 'delete my account'}
                >
                  {buttonText}
                </SecondaryButton>
                <WhiteButton onClick={closeModal}>Cancel</WhiteButton>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
